import React, { useState } from "react";
import { withRouter } from "next/router";
import { WithRouterProps } from "next/dist/client/with-router";
import { Button, InfoMessage, TitleSubtitle } from "../..";
import { ButtonContainer, ButtonIcon, ButtonLabel, CTABackAndForthContainer, StyledBlueSpan } from "../../../pages/Register/Register.styles";
import { CreateAddressContainer, EmailPreferenceContainer, EmailPreferenceSubTitle, EmailPreferenceTitle } from "./Address.styles";
import GoogleMapsAutoComplete from "../../GoogleMapsAutoComplete/GoogleMapsAutoComplete";
import getConfig from "next/config";
import { useRegisterContext } from "../../../contexts/RegisterContext";
import { setGeocoderInfo } from "../../../utils/google-location";
import { ButtonSwitch } from "../../ButtonSwitch/ButtonSwitch";
import { Tooltip } from "../../Tooltip/Tooltip";

interface AddressProps extends WithRouterProps {
    onNextButtonClick: Function;
    onPrevButtonClick: Function;
}

const Address: React.FC<AddressProps> = ({
    onNextButtonClick,
    onPrevButtonClick
}) => {
    const { registerInfo, setRegisterInfo, googleAddress, setGoogleAddress } = useRegisterContext();
    const [addressValidationError, setAddressValidationError] = useState<boolean>(false);
    const [addressValidationErrorMessage, setAddressValidationErrorMessage] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false)

    const { publicRuntimeConfig } = getConfig();

    const onAddressChange = async (value: any) => {
        setLoading(true)
        setGoogleAddress(value);
        var registerAddress = {
            latitude: null,
            longitude: null,
            longLocation: null,
            shortLocation: null,
            detailsJson: null,
            country: "",
            unitOrApartmentNumber: null,
            street: "",
            suburb: null,
            state: null,
            postCode: ""
        }
        await setGeocoderInfo(value, registerAddress)

        setRegisterInfo(prevRegisterInfo => ({
            ...prevRegisterInfo,
            address: registerAddress
        }));
        setLoading(false)
    }

    const handleNextButtonClick = () => {
        setAddressValidationError(false);
        if (!registerInfo.address.street) {
            setAddressValidationError(true);
            setAddressValidationErrorMessage("Street is mandatory");
            return;
        }
        onNextButtonClick()
    }

    return (
        <CreateAddressContainer data-cy-pageloading={loading}>
            <TitleSubtitle
                size="sm"
                title="Your Neck of the Woods"
                subTitle={<>Don't worry, we won't show up at your door.
                    There's an <Tooltip tooltipName="appearanceReleaseAndNDA"><StyledBlueSpan>Appearance Release and NDA</StyledBlueSpan></Tooltip> in the Terms and Conditions for each show.
                </>} />
            <GoogleMapsAutoComplete
                id="google-maps-autocomplete"
                apiKey={publicRuntimeConfig.googleMapsApiKey}
                value={googleAddress}
                onChange={onAddressChange}
                newUIUXStyle={true}
                data-cy="modal-shootinglocation-location"
            />
            {addressValidationError && (
                <InfoMessage
                    variant="error"
                    hasBackground={true}
                    message={addressValidationErrorMessage}
                />
            )}
            <EmailPreferenceContainer>
                <EmailPreferenceTitle>
                    Email me about future shows filming:
                </EmailPreferenceTitle>
                <ButtonSwitch
                    leftButtonChildren="Countrywide"
                    rightButtonChildren="In My City"
                    active={registerInfo.user.emailPreferences === "AustraliaWide" ? "left" : "right"}
                    onLeftButtonClick={() => {
                        setRegisterInfo(prevRegisterInfo => ({
                            ...prevRegisterInfo,
                            user: {
                                ...prevRegisterInfo.user,
                                emailPreferences: "AustraliaWide"
                            }
                        }))
                    }}
                    onRightButtonClick={() => {
                        setRegisterInfo(prevRegisterInfo => ({
                            ...prevRegisterInfo,
                            user: {
                                ...prevRegisterInfo.user,
                                emailPreferences: "InState"
                            }
                        }))
                    }}
                />
                <EmailPreferenceSubTitle>
                    We only email about upcoming shows. You can unsubscribe easily at any time.
                </EmailPreferenceSubTitle>
            </EmailPreferenceContainer>
            <CTABackAndForthContainer>
                <Button
                    size="md"
                    variant="tertiary"
                    onClick={() => onPrevButtonClick()}
                    data-cy="button-booking-next"
                    sx={{ padding: 0 }}
                >
                    <ButtonContainer>
                        <ButtonIcon icon="ArrowLeft" />
                        <ButtonLabel>Back</ButtonLabel>
                    </ButtonContainer>
                </Button>
                <Button
                    data-cy="register-address-submit"
                    onClick={handleNextButtonClick}
                    disabled={googleAddress == null}
                > Continue </Button>
            </CTABackAndForthContainer>
        </CreateAddressContainer>
    );
};

Address.displayName = "Address";

const ComponentWithRouter = withRouter(Address)

export { ComponentWithRouter as Address };
